import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import ReplayIcon from "@mui/icons-material/Replay"
import { Box, Chip, Grid, Paper, Stack, Typography } from "@mui/material"

import { ACTOR_META, AGENT_OUTPUT_FIELDS, AGENT_TOOLS } from "./guideContent"
import { Card, GuideSection } from "./guideParts"

const AGENT_COLOR = ACTOR_META.agent.color
const HOST_COLOR = ACTOR_META.host.color

function LoopBox({
  title,
  body,
  color
}: {
  title: string
  body: string
  color: string
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        borderColor: color,
        bgcolor: `${color}0A`,
        flex: 1,
        minWidth: 0
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 900, color }}>
        {title}
      </Typography>
      <Typography
        variant="caption"
        sx={{ color: "text.secondary", lineHeight: 1.45, display: "block" }}
      >
        {body}
      </Typography>
    </Paper>
  )
}

function Arrow() {
  return (
    <ArrowForwardIcon
      sx={{
        color: "text.disabled",
        alignSelf: "center",
        transform: { xs: "rotate(90deg)", md: "none" }
      }}
    />
  )
}

function AgentLoop() {
  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
        <LoopBox
          title="Talimat + mesajlar"
          body="Kodun hazırladığı talimat, konuşma hafızası ve müşterinin son mesajları."
          color={HOST_COLOR}
        />
        <Arrow />
        <LoopBox
          title="Düşün"
          body="Ajan bir model çağrısı yapar: cevap verebilir mi, yoksa bir bilgiye mi ihtiyacı var?"
          color={AGENT_COLOR}
        />
        <Arrow />
        <LoopBox
          title="Araç kullan"
          body="Gerekirse bir ya da birkaç aracı birlikte çağırır. Kod kuralları kontrol eder, sonucu geri verir."
          color={HOST_COLOR}
        />
        <Arrow />
        <LoopBox
          title="Cevap + karar"
          body="Yeterli bilgi olunca tek bir yapılandırılmış çıktı verir."
          color={AGENT_COLOR}
        />
      </Stack>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mt: 1.5, color: "text.secondary" }}
      >
        <ReplayIcon fontSize="small" />
        <Typography variant="caption">
          “Düşün → araç kullan” döngüsü gerektiği kadar tekrar eder. Sınırlar:
        </Typography>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {[
            "en fazla 10 model çağrısı",
            "60 saniye",
            "en fazla 3 bilgi araması"
          ].map((limit) => (
            <Chip key={limit} size="small" label={limit} />
          ))}
        </Stack>
      </Stack>
    </Paper>
  )
}

export default function AgentSection() {
  return (
    <GuideSection
      id="agent"
      eyebrow="4. adımın içi"
      title="Ajanın içinde: düşün, araç kullan, cevapla"
      lead="Ajan bir kez düşünüp cevap vermek zorunda değildir. Bilmediği bir şey varsa bir araç kullanır, sonucu okur ve yeniden düşünür. Hangi araçları görebileceğine ise kod karar verir: o tur için kapalı bir aracı ajan hiç görmez."
    >
      <AgentLoop />

      <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 1.5 }}>
        Ajanın araçları
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {AGENT_TOOLS.map((tool) => (
          <Grid item xs={12} md={6} lg={4} key={tool.name}>
            <Card accent={AGENT_COLOR}>
              <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                {tool.title}
              </Typography>
              <Typography
                variant="caption"
                component="code"
                sx={{ color: "text.secondary", display: "block", mb: 1 }}
              >
                {tool.name}
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.55, mb: 1.5 }}>
                {tool.what}
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontWeight: 800, display: "block" }}
              >
                Ne zaman verilir?
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", display: "block", mb: 1 }}
              >
                {tool.whenAvailable}
              </Typography>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 1,
                  bgcolor: `${HOST_COLOR}14`,
                  borderLeft: `3px solid ${HOST_COLOR}`
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 800, display: "block" }}
                >
                  Kodun kuralı
                </Typography>
                <Typography variant="caption" sx={{ lineHeight: 1.45 }}>
                  {tool.guardrail}
                </Typography>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 0.5 }}>
        Ajanın çıktısı
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>
        Her tur, bu alanlardan oluşan tek bir sonuçla biter. Kod sadece bu
        sonuca bakarak ne yapılacağına karar verir.
      </Typography>
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        {AGENT_OUTPUT_FIELDS.map((field, index) => (
          <Stack
            key={field.field}
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 0.25, sm: 2 }}
            sx={{
              px: 2,
              py: 1,
              borderTop: index ? 1 : 0,
              borderColor: "divider"
            }}
          >
            <Typography
              variant="body2"
              component="code"
              sx={{ fontWeight: 800, minWidth: 170 }}
            >
              {field.field}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {field.meaning}
            </Typography>
          </Stack>
        ))}
      </Paper>
    </GuideSection>
  )
}
